import React, { useEffect, useMemo, useRef, useState } from "react";

/**
 * Lightweight autocomplete/search+select.
 *
 * Props:
 *  - value: current selected id (string) or null
 *  - options: array of {id, name, meta?}
 *  - onChange(id)  // called with new id or null
 *  - placeholder, disabled, testId
 *  - allowEmpty (bool): show '—' clear pill
 */
export default function Autocomplete({
  value, options, onChange, onCreate, placeholder = "Ara…", disabled, testId, allowEmpty = false,
}) {
  const selected = options.find((o) => o.id === value) || null;
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    // Reset query when a value is picked externally
    setQuery("");
  }, [value]);

  useEffect(() => {
    const onDoc = (e) => {
      if (!rootRef.current) return;
      if (!rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options.slice(0, 30);
    return options
      .filter((o) => (o.name || "").toLowerCase().includes(q) || (o.meta || "").toLowerCase().includes(q))
      .slice(0, 30);
  }, [query, options]);

  const pick = (o) => {
    onChange(o ? o.id : null);
    setQuery("");
    setOpen(false);
  };

  return (
    <div ref={rootRef} className="relative">
      {selected ? (
        <div className="flex items-center gap-2 px-3 py-2 rounded-md bg-[#171717] border border-[#EA580C]/40">
          <span data-testid={testId ? `${testId}-selected` : undefined} className="flex-1 text-sm">
            {selected.name}
            {selected.meta ? <span className="text-slate-500 text-xs ml-2 font-mono">{selected.meta}</span> : null}
          </span>
          <button type="button" disabled={disabled} onClick={() => pick(null)}
            className="text-xs text-slate-400 hover:text-rose-300"
            data-testid={testId ? `${testId}-clear` : undefined}>
            Değiştir
          </button>
        </div>
      ) : (
        <>
          <input
            data-testid={testId}
            value={query}
            onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)}
            placeholder={placeholder}
            disabled={disabled}
            className="w-full px-3 py-2 rounded-md bg-[#171717] border border-[#262626] outline-none focus:border-[#EA580C] text-sm disabled:opacity-50"
          />
          {open && !disabled && (
            <div className="absolute z-20 mt-1 left-0 right-0 max-h-64 overflow-auto rounded-md bg-[#0F0F0F] border border-[#262626] shadow-lg">
              {allowEmpty && (
                <button type="button" onClick={() => pick(null)}
                  className="w-full text-left px-3 py-2 text-xs text-slate-400 hover:bg-[#171717]">
                  —
                </button>
              )}
              {filtered.length === 0 ? (
                <>
                  <div className="px-3 py-2 text-xs text-slate-500">Sonuç yok</div>
                  {onCreate && query.trim() && (
                    <button type="button"
                      data-testid={testId ? `${testId}-create` : undefined}
                      onClick={async () => {
                        const name = query.trim();
                        try {
                          const created = await onCreate(name);
                          if (created && created.id) {
                            pick({ id: created.id, name: created.name || name });
                          }
                        } catch (e) { /* handled by caller */ }
                      }}
                      className="w-full text-left px-3 py-2 text-sm text-[#EA580C] hover:bg-[#171717] border-t border-[#262626]">
                      + "{query.trim()}" oluştur
                    </button>
                  )}
                </>
              ) : filtered.map((o) => (
                <button key={o.id} type="button"
                  data-testid={testId ? `${testId}-opt-${o.id}` : undefined}
                  onClick={() => pick(o)}
                  className="w-full text-left px-3 py-2 hover:bg-[#171717] text-sm">
                  <div>{o.name}</div>
                  {o.meta && <div className="text-xs text-slate-500 font-mono">{o.meta}</div>}
                </button>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
